import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

// Eagerly load the home page for fast first paint
import Home from './pages/Home';

// Lazy load all other pages for smaller initial bundle
const Features = lazy(() => import('./pages/Features'));
const Compatibility = lazy(() => import('./pages/Compatibility'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Success = lazy(() => import('./pages/Success'));
const NDA = lazy(() => import('./pages/NDA'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminCustomerDetail = lazy(() => import('./pages/admin/AdminCustomerDetail'));
const AdminCI = lazy(() => import('./pages/admin/AdminCI'));
const AdminCIHistory = lazy(() => import('./pages/admin/AdminCIHistory'));
const AdminCIAnalytics = lazy(() => import('./pages/admin/AdminCIAnalytics'));
const AdminWaitlist = lazy(() => import('./pages/admin/AdminWaitlist'));
const AdminContactSubmissions = lazy(() => import('./pages/admin/AdminContactSubmissions'));

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #111f35 50%, #0a1628 100%)' }}>
      <div className="animate-spin rounded-full h-12 w-12" style={{ borderTop: '2px solid #d4af37', borderBottom: '2px solid #d4af37' }}></div>
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return user ? <>{children}</> : <Navigate to="/" />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;
  return <>{children}</>;
}

export default function App() {
  return (
    <div className="min-h-screen" style={{ background: '#0a1628' }}>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Public marketing pages */}
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/compatibility" element={<Compatibility />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/nda" element={<NDA />} />
          <Route path="/success" element={<Success />} />

          {/* Authenticated routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
          <Route path="/admin/customers/:id" element={<AdminRoute><AdminCustomerDetail /></AdminRoute>} />
          <Route path="/admin/ci" element={<AdminRoute><AdminCI /></AdminRoute>} />
          <Route path="/admin/ci/history" element={<AdminRoute><AdminCIHistory /></AdminRoute>} />
          <Route path="/admin/ci/analytics" element={<AdminRoute><AdminCIAnalytics /></AdminRoute>} />
          <Route path="/admin/waitlist" element={<AdminRoute><AdminWaitlist /></AdminRoute>} />
          <Route path="/admin/contact-submissions" element={<AdminRoute><AdminContactSubmissions /></AdminRoute>} />
        </Routes>
      </Suspense>
    </div>
  );
}
